import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User } from 'lucide-react';
import { Meta } from '@/components/seo/Meta';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Separator } from '@/components/ui/Separator';
import { FadeIn, Stagger, StaggerItem } from '@/components/motion/FadeIn';
import { Skeleton } from '@/components/ui/Skeleton';
import { TableOfContents, type TocItem } from '@/components/marketing/TableOfContents';
import { MarkdownWithAnchors, extractHeadings } from '@/components/marketing/MarkdownRenderer';
import { ShareButtons } from '@/components/marketing/ShareButtons';
import { usePostBySlug, useRelatedPosts } from '@/lib/cms';
import { formatDate, readingTime } from '@/lib/utils';

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: post, isLoading, isError } = usePostBySlug(slug);
  const { data: related = [] } = useRelatedPosts(post?.id, post?.category);

  if (isLoading) {
    return (
      <>
        <Meta title="Loading…" path={`/blog/${slug ?? ''}`} />
        <div className="pt-24 sm:pt-28 md:pt-32">
          <Skeleton className="h-[60vh] w-full" />
        </div>
      </>
    );
  }

  if (isError || !post) {
    return (
      <>
        <Meta title="Post not found" path={`/blog/${slug ?? ''}`} />
        <section className="flex min-h-[70vh] items-center justify-center pt-20">
          <div className="container-narrow text-center">
            <h1 className="display-serif text-display-l text-fg">Post not found.</h1>
            <p className="mt-3 text-fg-muted">It may have been unpublished or moved.</p>
            <div className="mt-6">
              <Button asChild>
                <Link to="/blog">
                  <ArrowLeft className="h-4 w-4" /> All posts
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </>
    );
  }

  const headings: TocItem[] = extractHeadings(post.body_markdown);
  const readMin = post.reading_time_min ?? readingTime(post.body_markdown);
  const canonicalUrl = `https://vonlof.co/blog/${post.slug}`;

  return (
    <>
      <Meta
        title={post.meta_title ?? post.title}
        description={post.meta_description ?? post.excerpt ?? undefined}
        path={`/blog/${post.slug}`}
        type="article"
        publishedTime={post.published_at ?? undefined}
        authors={post.author ? [post.author.name] : ['Vonlof']}
        image={post.hero_image_url ?? undefined}
        jsonLd={
          {
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: post.title,
            description: post.excerpt ?? undefined,
            datePublished: post.published_at ?? undefined,
            dateModified: post.updated_at ?? post.published_at ?? undefined,
            author: post.author
              ? { '@type': 'Person', name: post.author.name }
              : { '@type': 'Organization', name: 'Vonlof' },
            publisher: {
              '@type': 'Organization',
              name: 'Vonlof',
              logo: { '@type': 'ImageObject', url: 'https://vonlof.co/og/default.png' },
            },
            mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
            keywords: post.tags.join(', '),
          } as never
        }
      />
      <main className="pt-24 pb-12 sm:pt-28 md:pt-32 md:pb-20">
        <article className="container-narrow">
          <FadeIn>
            <Link
              to="/blog"
              className="inline-flex items-center gap-1 text-sm text-fg-muted transition-colors hover:text-gold-500"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> All posts
            </Link>
          </FadeIn>
          <FadeIn delay={0.05}>
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <Badge variant="gold">{post.category}</Badge>
              {post.tags.map((t) => (
                <Badge key={t} variant="outline">{t}</Badge>
              ))}
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <h1 className="mt-4 display-serif text-display-l text-fg">{post.title}</h1>
          </FadeIn>
          {post.excerpt && (
            <FadeIn delay={0.15}>
              <p className="mt-6 text-xl text-fg-muted">{post.excerpt}</p>
            </FadeIn>
          )}
          <FadeIn delay={0.2}>
            <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-fg-muted">
              {post.author && (
                <div className="flex items-center gap-2">
                  <Avatar className="h-7 w-7">
                    {post.author.portrait_url && (
                      <AvatarImage src={post.author.portrait_url} alt={post.author.name} />
                    )}
                    <AvatarFallback>{initials(post.author.name)}</AvatarFallback>
                  </Avatar>
                  <span className="inline-flex items-center gap-1.5">
                    <User className="h-3.5 w-3.5" /> {post.author.name}
                  </span>
                </div>
              )}
              {post.published_at && (
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" /> {formatDate(post.published_at)}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" /> {readMin} min read
              </span>
            </div>
          </FadeIn>
        </article>

        {post.hero_image_url && (
          <section className="mt-12">
            <div className="container-wide">
              <FadeIn>
                <div className="aspect-[16/9] overflow-hidden rounded-xl border border-border bg-bg-subtle">
                  <img
                    src={post.hero_image_url}
                    alt={`${post.title} hero image`}
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                    className="h-full w-full object-cover"
                  />
                </div>
              </FadeIn>
            </div>
          </section>
        )}

        {/* Body + TOC */}
        <section className="mt-12">
          <div className="container-default grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <FadeIn>
                <MarkdownWithAnchors content={post.body_markdown} />
              </FadeIn>
            </div>
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24">
                <TableOfContents items={headings} />
                <Separator className="my-8" />
                <ShareButtons url={canonicalUrl} title={post.title} />
              </div>
            </aside>
          </div>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-20">
            <div className="container-wide">
              <Separator className="mb-12" />
              <FadeIn>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Related</p>
                <h2 className="mt-3 text-h3 text-fg">More on {post.category}</h2>
              </FadeIn>
              <Stagger className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((p) => (
                  <StaggerItem key={p.id}>
                    <Link to={`/blog/${p.slug}`} className="group block h-full">
                      <Card className="h-full transition-all hover:-translate-y-1 hover:border-gold-500/40 hover:shadow-gold">
                        <div className="relative aspect-video w-full overflow-hidden bg-gradient-to-br from-gold-500/20 to-bg-elevated">
                          {p.hero_image_url ? (
                            <img
                              src={p.hero_image_url}
                              alt={`${p.title} hero`}
                              loading="lazy"
                              className="h-full w-full object-cover transition-transform duration-slow group-hover:scale-105"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-xs uppercase tracking-widest text-fg-subtle">
                              {p.category}
                            </div>
                          )}
                        </div>
                        <CardHeader>
                          <Badge variant="gold">{p.category}</Badge>
                          <CardTitle className="mt-2 line-clamp-2">{p.title}</CardTitle>
                          {p.excerpt && <CardDescription className="line-clamp-2">{p.excerpt}</CardDescription>}
                        </CardHeader>
                      </Card>
                    </Link>
                  </StaggerItem>
                ))}
              </Stagger>
            </div>
          </section>
        )}
      </main>
    </>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}